import React, {useEffect} from 'react';
import {createRoot} from 'react-dom/client';
import {DocumentTemplate} from 'types/documentTemplate';
import {DocumentTemplateRow} from 'types/documentTemplateRow';
import {DocumentTemplateColumn} from 'types/documentTemplateColumn';
import RenderElementContent from './RenderElementContent';
import {Provider} from 'react-redux';
import {store} from '../../store/store';

interface CompanyInfo {
  logoUrl: string;
  name: string;
}

interface DocumentTemplateRendererProps {
  template: DocumentTemplate;
  mode?: 'view' | 'download';
  className?: string;
  companyInfo: CompanyInfo;
  onPrintComplete?: () => void;
}

const DocumentTemplateRenderer: React.FC<DocumentTemplateRendererProps> = ({
  template,
  mode = 'view',
  className = '',
  companyInfo,
  onPrintComplete,
}) => {
  useEffect(() => {
    if (mode === 'download') {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        if (onPrintComplete) onPrintComplete();
        return;
      }

      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            return Array.from(styleSheet.cssRules)
              .map(rule => rule.cssText)
              .join('\n');
          } catch (e) {
            return '';
          }
        })
        .join('\n');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${template.name}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              /* Base styles */
              body { 
                margin: 0; 
                padding: 20px;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
              }

              /* Tailwind styles */
              ${styles}

              /* Print specific styles */
              @media print {
                body {
                  padding: 0;
                  margin: 0;
                }
                .document-template-print {
                  width: 100%;
                  padding: 20px;
                }
                .document-template-print img {
                  max-width: 200px;
                  height: auto;
                }
                .document-template-print table {
                  width: 100%;
                  border-collapse: collapse;
                }
                .document-template-print td, 
                .document-template-print th {
                  border: 1px solid #ddd;
                  padding: 8px;
                }
                .prose {
                  max-width: none;
                }
                .prose img {
                  margin: 0;
                }
                .prose p {
                  margin: 0;
                }
                .prose label {
                  display: block;
                  margin-bottom: 0.5rem;
                }
                .signature-image {
                  max-width: 200px;
                  height: auto;
                }
              }

              /* Document specific styles */
              .document-template-print {
                background-color: white;
                color: #1f2937;
              }
              .document-template-print .prose {
                color: #1f2937;
              }
              .document-template-print .prose label {
                color: #374151;
                font-weight: 600;
              }
              .document-template-print .prose img {
                display: block;
                margin: 0;
              }
              .document-template-print table {
                width: 100%;
                border-collapse: collapse;
                margin: 1rem 0;
              }
              .document-template-print th,
              .document-template-print td {
                border: 1px solid #e5e7eb;
                padding: 0.5rem;
                text-align: left;
              }
              .document-template-print th {
                background-color: #f9fafb;
                font-weight: 600;
              }
            </style>
          </head>
          <body>
            <div id="root"></div>
          </body>
        </html>
      `);

      printWindow.document.close();

      const root = printWindow.document.getElementById('root');
      if (root) {
        const reactRoot = createRoot(root);
        reactRoot.render(
          <Provider store={store}>
            <DocumentTemplateRenderer
              template={template}
              mode="view"
              companyInfo={companyInfo}
              onPrintComplete={() => {
                if (onPrintComplete) onPrintComplete();
                reactRoot.unmount();
                printWindow.close();
              }}
            />
          </Provider>,
        );

        // Wait for content to load before printing
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => {
              if (onPrintComplete) onPrintComplete();
              printWindow.close();
            };
          }, 500);
        };
      } else {
        if (onPrintComplete) onPrintComplete();
        printWindow.close();
      }
    }
  }, [mode, template, companyInfo, onPrintComplete]);

  const renderTemplateContent = () => {
    return (
      <div
        className={`px-8 py-8 bg-white w-full dark:bg-neutral-800 dark:border-neutral-700 ${className}`}>
        {template.enableHeader && (
          <div className="flex mb-4">
            <div className="flex-1 px-5 relative">
              <div className="w-full h-full min-h-20 flex items-center justify-start">
                <div className="text-slate-600 text-lg font-medium font-['Inter']">
                  <img
                    className="pl-3 w-24 h-auto"
                    src={companyInfo.logoUrl}
                    alt="Logo"
                  />
                </div>
                <div className="text-slate-400 text-md font-light font-['Inter'] leading-normal ml-1 mr-1">
                  |
                </div>
                <div className="text-slate-400 text-md font-light font-['Inter'] leading-normal">
                  {template.name}
                </div>
              </div>
            </div>
          </div>
        )}

        {(template.rows || []).map((row: DocumentTemplateRow) => {
          if (row._destroy) {
            return null;
          }
          return (
            <div key={row.id} className="flex mb-4">
              {row.columns.map((col: DocumentTemplateColumn) => (
                <div key={col.id} className={`flex-1 px-4`}>
                  {col.element && col.element._destroy !== true ? (
                    <div className="w-full h-full min-h-20 flex items-start justify-start">
                      <RenderElementContent element={col.element} />
                    </div>
                  ) : (
                    <div className="w-full h-full min-h-20 flex items-center justify-center text-slate-400"></div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  if (mode === 'download') {
    return null;
  }

  return renderTemplateContent();
};

export default DocumentTemplateRenderer;
